import { arrayMove } from '@dnd-kit/sortable';
import { useContext, useEffect, useState } from 'react';

import {
  useLazyGetBranchEmployeesOrderQuery,
  useSetBranchEmployeesOrderMutation,
} from '@app/api/Employees';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NEmployees } from '@app/types/Employees';

import type { DragEndEvent } from '@dnd-kit/core';

export const useSortEmployees = () => {
  const [employees, setEmployees] = useState<
    NEmployees.ModelEmployeeOrderForFront[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [branchName, setBranchName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId } = useUserPrint();

  const [getBranchEmployeesOrder] = useLazyGetBranchEmployeesOrderQuery();
  const [setBranchEmployeesOrder] = useSetBranchEmployeesOrderMutation();

  useEffect(() => {
    const fetchBranchEmployeesOrder = async () => {
      if (!activeBranchId) {
        showNotification(
          'Произошла ошибка при определении идентификатора филиала',
          FRONT_WARNING
        );
        return;
      }

      try {
        setIsLoading(true);

        const result = await getBranchEmployeesOrder({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        const employees = (result.Employees || []).map(
          (employee: NEmployees.ModelEmployeeOrder) => {
            return { ...employee, Key: String(employee.OrderIndex) };
          }
        );

        setBranchName(result.Branch || '');
        setEmployees(employees);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranchEmployeesOrder();
  }, [activeBranchId, getBranchEmployeesOrder, showNotification]);

  const handleSortByName = () => {
    handleSort('Employee');
  };

  const handleSortByPosition = () => {
    handleSort('Position');
  };

  const handleSort = (field: 'Employee' | 'Position') => {
    setEmployees((prev: NEmployees.ModelEmployeeOrderForFront[]) => {
      const deepCopiedArray: NEmployees.ModelEmployeeOrderForFront[] =
        JSON.parse(JSON.stringify(prev));

      deepCopiedArray.sort((a, b) => {
        const nameA = a[field]?.toLowerCase() ? a[field]?.toLowerCase() : null;
        const nameB = b[field]?.toLowerCase() ? b[field]?.toLowerCase() : null;

        if (nameA === null) return 1;
        if (nameB === null) return -1;

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }

        return 0;
      });

      const sortedArray = deepCopiedArray.map((item, index) => {
        return {
          ...item,
          OrderIndex: index + 1,
        };
      });

      return sortedArray;
    });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setEmployees((previous) => {
        const activeIndex = previous.findIndex((i) => i.Key === active.id);
        const overIndex = previous.findIndex((i) => i.Key === over?.id);
        const newArray = arrayMove(previous, activeIndex, overIndex);
        const sortedArray = newArray.map((item, index) => {
          return {
            ...item,
            OrderIndex: index + 1,
          };
        });
        return sortedArray;
      });
    }
  };

  const saveEmployees = async () => {
    if (!activeBranchId) {
      showNotification(
        'Произошла ошибка при определении идентификатора филиала',
        FRONT_WARNING
      );
      return;
    }

    try {
      const data = employees.map((employee) => {
        return {
          ObjectId: employee.EmployeeId,
          OrderIndex: employee.OrderIndex,
        };
      });

      setIsSaving(true);
      setIsLoading(true);

      await setBranchEmployeesOrder({
        ActiveBranchId: activeBranchId,
        data,
      }).unwrap();

      showNotification('Порядок сотрудников обновлен успешно', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      setIsSaving(false);
      setIsLoading(false);
    }
  };

  return {
    handleSortByName,
    employees,
    handleSortByPosition,
    onDragEnd,
    isLoading,
    branchName,
    saveEmployees,
    isSaving,
  };
};
